import { Modal, Button, Stack, MultiSelect, NumberInput, TextInput, Box, Text, Group, ScrollArea, Accordion, Badge, Code } from '@mantine/core';
import { usePostApiEmbeddingCollectionTestSearch, useGetApiEmbeddingCollectionList } from '@/API';
import { useState } from 'react';
import { IconSearch, IconDatabase } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

interface EmbeddingCollectionTestSearchModalProps {
    workspaceId: string;
    opened: boolean;
    onClose: () => void;
}

export function EmbeddingCollectionTestSearchModal({ workspaceId, opened, onClose }: EmbeddingCollectionTestSearchModalProps) {
    const list = useGetApiEmbeddingCollectionList({ workspaceId, skip: 0, limit: 1000 });
    const collectionOptions = list.data?.data.results?.map(c => ({ value: c.id!, label: c.name! })) ?? [];

    const [selectedCollections, setSelectedCollections] = useState<string[]>([]);
    const [query, setQuery] = useState('');
    const [denseWeight, setDenseWeight] = useState<number | string>(1.0);
    const [sparseWeight, setSparseWeight] = useState<number | string>(1.0);
    const [limit, setLimit] = useState<number | string>(10);

    const testSearchMutation = usePostApiEmbeddingCollectionTestSearch();

    return (
        <Modal opened={opened} onClose={onClose} title="測試資料集合搜尋" size="xl" scrollAreaComponent={ScrollArea.Autosize}>
            <Stack gap="md">
                <MultiSelect
                    label="選擇搜尋資料集合"
                    placeholder="可選擇多個資料集合"
                    data={collectionOptions}
                    value={selectedCollections}
                    onChange={setSelectedCollections}
                    searchable
                    clearable
                />

                <Group grow>
                    <NumberInput
                        label="語意搜尋權重"
                        description="通常為向量相似度"
                        value={denseWeight}
                        onChange={setDenseWeight}
                        min={0}
                        max={10}
                        step={0.1}
                    />
                    <NumberInput
                        label="關鍵字搜尋權重"
                        description="文字的直接匹配"
                        value={sparseWeight}
                        onChange={setSparseWeight}
                        min={0}
                        max={10}
                        step={0.1}
                    />
                </Group>

                <Group grow>
                    <NumberInput
                        label="回傳筆數 (Limit)"
                        value={limit}
                        onChange={setLimit}
                        min={1}
                        max={100}
                        step={1}
                    />
                </Group>

                <TextInput
                    label="測試搜尋字詞"
                    placeholder="輸入關鍵字..."
                    value={query}
                    onChange={(e) => setQuery(e.currentTarget.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && query && selectedCollections.length > 0) {
                            testSearchMutation.mutate({
                                params: { workspaceId },
                                data: {
                                    query,
                                    collectionIds: selectedCollections,
                                    denseWeight: Number(denseWeight),
                                    sparseWeight: Number(sparseWeight),
                                    limit: Number(limit)
                                }
                            }, {
                                onError: () => notifications.show({ message: "搜尋失敗", color: "red" })
                            });
                        }
                    }}
                    rightSectionWidth={50}
                    rightSection={
                        <Button
                            loading={testSearchMutation.isPending}
                            onClick={() => {
                                if (!query || selectedCollections.length === 0) {
                                    notifications.show({ message: "請輸入關鍵字並選擇至少一個資料集合", color: "yellow" });
                                    return;
                                }
                                testSearchMutation.mutate({
                                    params: { workspaceId },
                                    data: {
                                        query,
                                        collectionIds: selectedCollections,
                                        denseWeight: Number(denseWeight),
                                        sparseWeight: Number(sparseWeight),
                                        limit: Number(limit)
                                    }
                                }, {
                                    onError: () => notifications.show({ message: "搜尋失敗", color: "red" })
                                });
                            }}
                            variant="filled"
                            size="xs"
                        >
                            <IconSearch size={16} />
                        </Button>
                    }
                />

                {testSearchMutation.data?.data && (
                    <Box mt="md">
                        <Text fw={500} mb="xs">搜尋結果 ({testSearchMutation.data.data.length} 筆)</Text>
                        {testSearchMutation.data.data.length === 0 ? (
                            <Text c="dimmed">沒有找到相關結果</Text>
                        ) : (
                            <Accordion variant="separated">
                                {testSearchMutation.data.data.map((result, index) => {
                                    const colName = collectionOptions.find(c => c.value === result.embeddingCollectionId)?.label ?? result.embeddingCollectionId;
                                    return (
                                        <Accordion.Item key={index} value={`item-${index}`}>
                                            <Accordion.Control>
                                                <Group wrap="nowrap">
                                                    <Badge color="blue" variant="light">分數: {result.score?.toFixed(4)}</Badge>
                                                    <Badge color="grape" variant="light" leftSection={<IconDatabase size={12} />}>{colName}</Badge>
                                                    <Text truncate w={400}>{result.text?.slice(0, 50)}...</Text>
                                                </Group>
                                            </Accordion.Control>
                                            <Accordion.Panel>
                                                <Stack gap="sm">
                                                    <Box>
                                                        <Text fw={500} size="sm">文字內容 (Text):</Text>
                                                        <Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word', }} p="sm" bg="gray.0">
                                                            {result.text}
                                                        </Text>
                                                    </Box>
                                                    {result.payload && (
                                                        <Box>
                                                            <Text fw={500} size="sm">Metadata (Payload):</Text>
                                                            <Code block style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word', }}>
                                                                {JSON.stringify(result.payload, null, 2)}
                                                            </Code>
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </Accordion.Panel>
                                        </Accordion.Item>
                                    );
                                })}
                            </Accordion>
                        )}
                    </Box>
                )}
            </Stack>
        </Modal>
    );
}